import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Workspaces } from "@mui/icons-material";
import { Alert, Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import * as Sentry from "@sentry/react";
import { enqueueSnackbar } from "notistack";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import {
  useCreateVersionCoreConfigurationMutation,
  useCreateVersionScenarioConfigurationMutation,
  useUpdateVersionCoreConfigurationMutation,
  useUpdateVersionScenarioConfigurationMutation,
} from "fond/api";
import { Maybe, Store, VersionCoreConfiguration, VersionScenarioConfiguration } from "fond/types";
import { Modal } from "fond/widgets";

import { convertFormDataToCoreConfig, convertFormDataToScenarioConfig } from "./helper";
import SetupForm, { FormData } from "./SetupForm";

interface SetupModalProps {
  coreConfiguration: Maybe<VersionCoreConfiguration>;
  scenarioConfiguration: Maybe<VersionScenarioConfiguration>;
  isReadOnly: boolean;
  onClose: () => void;
}

export const Steps = {
  UPLOAD: 0,
  LAYER_SELECTION: 1,
  CONFIGURATION: 2,
} as const;
export type Steps = (typeof Steps)[keyof typeof Steps];

const steps = ["Upload Artifacts", "Associate Layers", "Configuration"];

const SetupModal: React.FC<SetupModalProps> = ({ coreConfiguration, scenarioConfiguration, onClose, isReadOnly }) => {
  const [activeStep, setActiveStep] = useState<Steps>(Steps.UPLOAD);
  const [isSaving, setIsSaving] = useState(false);
  const versionId = useSelector((state: Store) => state.project.versionId);
  const [createVersionScenarioConfiguration] = useCreateVersionScenarioConfigurationMutation();
  const [updateVersionScenarioConfiguration] = useUpdateVersionScenarioConfigurationMutation();
  const [createVersionCoreConfiguration] = useCreateVersionCoreConfigurationMutation();
  const [updateVersionCoreConfiguration] = useUpdateVersionCoreConfigurationMutation();

  const handleOnCancel = useCallback(() => {
    analytics.track("collaboration.cts.setup.cancelled", {
      versionId,
      currentStep: steps[activeStep],
      isReadOnly,
    });
    onClose();
  }, [versionId, activeStep, isReadOnly, onClose]);

  const handleOnSubmit = useCallback(
    async (formData: FormData) => {
      const scenarioPayload = convertFormDataToScenarioConfig(formData);
      const corePayload = convertFormDataToCoreConfig(formData);
      setIsSaving(true);
      const isNew = !coreConfiguration && !scenarioConfiguration;
      const analyticsPayload = {
        versionId,
        isNew,
        hasOccupancyFile: scenarioPayload.Inputs.Occupancy.Type === "file",
        useDefaultBins: formData.DefaultBins,
        binCount: scenarioPayload.ToolConfigurations.CostToServe.BinCount,
        layerMappingsCount: corePayload.LayerMappings.length,
      };

      try {
        await Promise.all([
          coreConfiguration
            ? updateVersionCoreConfiguration({ versionId: versionId!, configuration: corePayload }).unwrap()
            : createVersionCoreConfiguration({ versionId: versionId!, configuration: corePayload }).unwrap(),
          scenarioConfiguration
            ? updateVersionScenarioConfiguration({ versionId: versionId!, configuration: scenarioPayload }).unwrap()
            : createVersionScenarioConfiguration({ versionId: versionId!, configuration: scenarioPayload }).unwrap(),
        ]);
        analytics.track("collaboration.cts.configuration.saved", analyticsPayload);
        enqueueSnackbar("Cost to Serve configuration updated", { variant: "success" });
        onClose();
      } catch (error) {
        Sentry.captureException(error);
        enqueueSnackbar(`Failed to ${isNew ? "create" : "update"} Cost to Serve configuration`, { variant: "error" });
      } finally {
        setIsSaving(false);
      }
    },
    [
      coreConfiguration,
      scenarioConfiguration,
      createVersionCoreConfiguration,
      createVersionScenarioConfiguration,
      onClose,
      updateVersionCoreConfiguration,
      updateVersionScenarioConfiguration,
      versionId,
    ]
  );

  const handleNext = useCallback(() => {
    setActiveStep((prev) => (prev + 1) as Steps);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => (prev - 1) as Steps);
  }, []);

  return (
    <Modal
      open
      disableBackdropClick
      variant="primary"
      header="Import"
      headerIcon={<Workspaces />}
      headerSubtitle="COLLABORATION - COST TO SERVE"
      onClose={handleOnCancel}
      showClose
      showFullscreen
      data-testid="cost-to-serve-settings"
      PaperProps={{ style: { minHeight: "60vh", minWidth: "60vw", maxHeight: "90vh", display: "flex", flexDirection: "column" } }}
      DialogContentProps={{
        sx: {
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        },
      }}
      content={
        <>
          {isReadOnly && (
            <Box mb={2}>
              <Alert severity="info">This configuration is read-only.</Alert>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 4, flex: 1, minHeight: 0 }}>
            <Box sx={{ minWidth: 175 }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label, index) => (
                  <Step key={label} completed={activeStep > index || isReadOnly}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto" }} className="customScrollbars">
              <SetupForm
                activeStep={activeStep}
                onNext={handleNext}
                onSubmit={handleOnSubmit}
                coreConfiguration={coreConfiguration}
                scenarioConfiguration={scenarioConfiguration}
                isReadOnly={isReadOnly}
              />
            </Box>
          </Box>
        </>
      }
      actions={
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Button onClick={handleOnCancel} data-testid="close-button">
            Close
          </Button>
          <Box>
            {activeStep !== Steps.UPLOAD && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            {activeStep === Steps.CONFIGURATION && isReadOnly ? (
              <Button variant="contained" onClick={handleOnCancel} data-testid="readonly-finish-button">
                Finish
              </Button>
            ) : (
              <LoadingButton variant="contained" form="collaboration-cts-form" type="submit" loading={isSaving}>
                {activeStep === Steps.CONFIGURATION ? "Finish" : "Next"}
              </LoadingButton>
            )}
          </Box>
        </Box>
      }
    />
  );
};

export default SetupModal;
